import { useCallback, useEffect, useMemo, useState } from "react";
import { getComments, deleteComment } from "../../../services/comments.service";
import { CommentItem } from "../models/comments.model";

type SnackbarState = {
  open: boolean;
  text: string;
  severity: "success" | "error";
};

export function useCommentsTable() {
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const [comments, setComments] = useState<CommentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingComment, setViewingComment] = useState<CommentItem | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingComment, setDeletingComment] = useState<CommentItem | null>(null);

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    text: "",
    severity: "success",
  });

  const fetchComments = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const data = await getComments();
      setComments(data);
    } catch (e) {
      console.error("Error fetching comments", e);
      setErrorMsg("Error al cargar los comentarios.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // ---- Filtro por searchTerm
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredComments = useMemo(() => {
    if (!normalizedSearch) return comments;
    return comments.filter((c) => {
      const id = c.id?.toString() ?? "";
      const comment = c.comment?.toLowerCase() ?? "";
      const source = (c as any).source?.toLowerCase?.() ?? "";
      const userName = (c as any).userName?.toLowerCase?.() ?? "";
      return (
        id.includes(normalizedSearch) ||
        comment.includes(normalizedSearch) ||
        source.includes(normalizedSearch) ||
        userName.includes(normalizedSearch)
      );
    });
  }, [comments, normalizedSearch]);

  // ---- Total filtrado
  const total = filteredComments.length;

  // ---- Asegurar que la página no quede fuera de rango cuando cambian filtros o rowsPerPage
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
    setPage((prev) => Math.min(prev, totalPages));
  }, [total, rowsPerPage]);

  // ---- Paginación (slice)
  const pagedComments = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredComments.slice(start, end);
  }, [filteredComments, page, rowsPerPage]);

  const handleChangeSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleChangeRowsPerPage = (value: number) => {
    setRowsPerPage(value);
    setPage(1);
  };

  const openView = (item: CommentItem) => {
    setViewingComment(item);
    setViewDialogOpen(true);
  };
  const closeView = () => setViewDialogOpen(false);

  const confirmDelete = (item: CommentItem) => {
    setDeletingComment(item);
    setDeleteDialogOpen(true);
  };
  const closeDelete = () => setDeleteDialogOpen(false);

  const doDelete = async () => {
    if (!deletingComment) return;
    try {
      const ok = await deleteComment(deletingComment.id);
      if (!ok) throw new Error("delete failed");

      setSnackbar({ open: true, text: "Comentario eliminado.", severity: "success" });
      setDeleteDialogOpen(false);
      setDeletingComment(null);

      // Si eliminaste el último de la página, retrocede una página
      const isLastItemOnPage = pagedComments.length === 1 && page > 1;
      if (isLastItemOnPage) {
        setPage((p) => Math.max(1, p - 1));
      } else {
        fetchComments();
      }
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, text: "No se pudo eliminar el comentario.", severity: "error" });
    }
  };

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return {
    // datos listos para pintar
    comments: pagedComments,
    total,

    // estado ui
    loading,
    errorMsg,
    snackbar,

    // filtros/paginación
    searchTerm,
    rowsPerPage,
    page,
    setPage,
    handleChangeSearch,
    handleChangeRowsPerPage,

    // ver
    viewDialogOpen,
    viewingComment,
    openView,
    closeView,

    // delete
    deleteDialogOpen,
    deletingComment,
    confirmDelete,
    closeDelete,
    doDelete,

    // snackbar
    closeSnackbar,
  };
}

export type UseCommentsTableReturn = ReturnType<typeof useCommentsTable>;
